import { IBotData } from "../shared/types";
import { pairPriceLabel, roundUsdForDisplay } from "./formatters";

type Stats = IBotData["stats"];

/**
 * Single source of truth for bot state and headline metrics, so every view
 * (sidebar, tabs, cards, rings, hero) tells the same story.
 */

export type BotState = "in-range" | "out-of-range" | "balancing" | "idle" | "disabled";

const isRskBot = (botName: string) => botName.toUpperCase().startsWith("RSK");

export const isBalancingBot = (s: Stats): boolean => (s.balancingUSD || 0) + (s.lentUSD || 0) > 0;

/** Price the range is compared against (pool tick price when available). */
export const rangePrice = (s: Stats): number => s.poolPrice ?? s.currentPrice;

/** Open position AND price within its own range. */
export const isInRange = (s: Stats): boolean => {
  if (!s.isPositionOpen) return false;
  const price = rangePrice(s);
  const { rangeLower, rangeUpper } = s;
  if (![price, rangeLower, rangeUpper].every((v) => Number.isFinite(v)) || !(rangeUpper > rangeLower)) return true;
  return price >= rangeLower && price <= rangeUpper;
};

export const botState = (s: Stats): BotState => {
  if (isBalancingBot(s)) return "balancing";
  if (s.isPositionOpen) return isInRange(s) ? "in-range" : "out-of-range";
  return s.enabled === false ? "disabled" : "idle";
};

export const BOT_STATE_LABEL: Record<BotState, string> = {
  "in-range": "In range",
  "out-of-range": "Out of range",
  balancing: "Balancing",
  idle: "No position",
  disabled: "Disabled",
};

/** Severity scale shared by every status indicator. */
export const BOT_STATE_TONE: Record<BotState, "success" | "error" | "brand" | "neutral"> = {
  "in-range": "success",
  "out-of-range": "error",
  balancing: "brand",
  idle: "error",
  disabled: "neutral",
};

/** Capital basis used by the backend for returns: initial + deposits − withdrawals. */
export const capitalBasis = (s: Pick<Stats, "initialCapital" | "depositsUSD" | "withdrawalsUSD">): number =>
  (s.initialCapital || 0) + (s.depositsUSD || 0) - (s.withdrawalsUSD || 0);

export const returnPct = (pnl: number, basis: number): number => (basis > 0 ? pnl / basis : 0);

/**
 * Returns only mean something on a positive capital basis: a bot that withdrew more than it put
 * in (profits taken out) has basis ≤ 0, and the backend reports 0% for it. Aggregate percentages
 * therefore use only bots with a positive basis — their P&L over their basis — while USD totals
 * keep every bot.
 */
export const hasReturnBasis = (s: Pick<Stats, "initialCapital" | "depositsUSD" | "withdrawalsUSD">): boolean =>
  capitalBasis(s) > 0;

/** Σ P&L / Σ basis over bots with a positive basis (0 when none). */
export const aggregateReturn = (list: Stats[]): number => {
  const ok = list.filter(hasReturnBasis);
  return returnPct(
    ok.reduce((acc, s) => acc + (s.pnlUSD || 0), 0),
    ok.reduce((acc, s) => acc + capitalBasis(s), 0)
  );
};

/** Σ positive bases: the capital the aggregate percentages are measured on. */
export const returnBasisTotal = (list: Stats[]): number =>
  list.filter(hasReturnBasis).reduce((acc, s) => acc + capitalBasis(s), 0);

/**
 * HODL benchmark the backend used for this bot: the deposited tokens valued in USD when it sends
 * `hodlValueUSD`, otherwise the pair price change (older backends).
 */
export const usesHodlValue = (s: Stats): boolean => Number.isFinite(s.hodlValueUSD) && (s.hodlValueUSD as number) > 0;

/** Return of the HODL benchmark since inception (same basis as the LP return). */
export const hodlReturn = (s: Stats): number =>
  usesHodlValue(s) ? returnPct((s.hodlValueUSD as number) - capitalBasis(s), capitalBasis(s)) : s.priceChangePercent;

/** Short name of the benchmark for labels: "tokens deposited", or what the pair price measures. */
export const hodlBenchmarkLabel = (bots: Stats[], mixedPairs = false): string => {
  if (bots.length > 0 && bots.every(usesHodlValue)) return "tokens deposited";
  if (mixedPairs || bots.length === 0) return "pair prices";
  return pairPriceLabel(bots[0].token0Symbol, bots[0].token1Symbol);
};

const TOKEN_ALIASES: Record<string, string> = { WETH: "ETH", WBTC: "BTC", WRBTC: "BTC", RBTC: "BTC" };
const canonicalToken = (sym?: string) => {
  const u = (sym || "").trim().toUpperCase();
  return TOKEN_ALIASES[u] ?? u;
};

/**
 * A buyback that counts as treasury for this bot (backend rule): a buy (not RIF_SELL) of one of the
 * bot's own pair tokens, wrapped/native aliases included; RSK bots never count treasury.
 */
export const isTreasuryBuyback = (s: Stats, b: { taskType?: string; baseAsset: string }): boolean =>
  !isRskBot(s.botName) &&
  b.taskType !== "RIF_SELL" &&
  [canonicalToken(s.token0Symbol), canonicalToken(s.token1Symbol)].includes(canonicalToken(b.baseAsset));

/**
 * Bonus yield = USD value of repurchased tokens (non-RSK bots) + manual premiums.
 * `repurchaseCurValue` is the backend's USD value (amount × token USD price), the same figure
 * it adds to total capital; amount × currentPrice would use the pair price (WETH per FET…).
 */
export const bonusYieldUsd = (s: Stats): number =>
  (s.manualPremiums || 0) + (isRskBot(s.botName) ? 0 : s.repurchaseCurValue || 0);

/**
 * Net P&L split into parts that add up: pool fees + bonus yield (+ lending) + market, where
 * "market" is the residual (price moves, impermanent loss, rebalancing costs, gas).
 * The same breakdown as displayed: each part rounded like the cards round it and the residual
 * taken from those rounded parts, so the rows always add up to the displayed net (no $1 drift).
 */
export const pnlBreakdownDisplayed = (pnl: number, fees: number, bonus: number, lending = 0) => {
  const parts = { fees: roundUsdForDisplay(fees), bonus: roundUsdForDisplay(bonus), lending: roundUsdForDisplay(lending) };
  return { ...parts, market: roundUsdForDisplay(pnl) - parts.fees - parts.bonus - parts.lending };
};

export const MARKET_RESIDUAL_HELP =
  "Residual: net change − pool fees − bonus yield − lending. Price moves, impermanent loss, rebalancing and gas.";

/** Gas needed for a pending transaction is critical; a balance below the recommended one is a warning. */
export const gasSeverity = (s: Stats): "error" | "warning" =>
  (s.gasRequests || []).some((r) => r.source === "transaction") ? "error" : "warning";

/**
 * Worst state of a group of bots on the shared scale: red (out of range / no position) >
 * gold (balancing) > green (all in range); neutral when every bot is disabled.
 */
export const worstStateTone = (list: Stats[]): "error" | "brand" | "success" | "neutral" => {
  const states = list.map(botState);
  if (states.some((st) => st === "out-of-range" || st === "idle")) return "error";
  if (states.some((st) => st === "balancing")) return "brand";
  if (states.some((st) => st === "in-range")) return "success";
  return "neutral";
};

/** Counts per state, in severity order for strips and legends. */
export const STATE_ORDER: BotState[] = ["out-of-range", "idle", "balancing", "in-range", "disabled"];

export const countStates = (bots: IBotData[]): Record<BotState, number> => {
  const counts: Record<BotState, number> = { "in-range": 0, "out-of-range": 0, balancing: 0, idle: 0, disabled: 0 };
  bots.forEach((b) => {
    counts[botState(b.stats)] += 1;
  });
  return counts;
};
